/**
 * Capability checks. The server's permission list (from /identity/me) is the only source of truth —
 * never branch on role names (doc 01 §1.3). The UI hides what the user can't do; the API still enforces.
 *
 * Patterns:
 *  - exact:   "crm.customer.create"
 *  - prefix:  "finance.*"  → any permission in that module (used for nav sections of modules still in development)
 */
export type PermissionSet = ReadonlySet<string> | readonly string[] | null | undefined;

function toSet(perms: PermissionSet): ReadonlySet<string> {
  if (!perms) return new Set();
  return perms instanceof Set ? perms : new Set(perms as readonly string[]);
}

export function hasPerm(perms: PermissionSet, required: string): boolean {
  const set = toSet(perms);
  if (required.endsWith('.*')) {
    const prefix = required.slice(0, -1); // keep the dot
    for (const p of set) if (p.startsWith(prefix)) return true;
    return false;
  }
  return set.has(required);
}

/** True when the user holds at least one (empty list → true: no requirement). */
export function hasAnyPerm(perms: PermissionSet, anyOf: readonly string[] | undefined): boolean {
  if (!anyOf || anyOf.length === 0) return true;
  return anyOf.some((p) => hasPerm(perms, p));
}

export function hasAllPerms(perms: PermissionSet, allOf: readonly string[]): boolean {
  return allOf.every((p) => hasPerm(perms, p));
}

/** Nguyên Bình platform-operator permission (never granted by tenant roles). */
export const PLATFORM_PERM = 'platform.tenants.manage';

/** A platform admin without any business permission: sees only the platform console. */
export function isPlatformOnly(perms: PermissionSet): boolean {
  const set = toSet(perms);
  if (!set.has(PLATFORM_PERM)) return false;
  for (const p of set) if (p !== PLATFORM_PERM) return false;
  return true;
}

/** Permission codes used by the web app (mirror of apps/api Platform/Security.cs + module permissions). */
export const P = {
  platformTenantsManage: 'platform.tenants.manage',
  usersManage: 'identity.users.manage',
  rolesManage: 'identity.roles.manage',
  delegationsManage: 'identity.delegations.manage',
  delegationsOwn: 'identity.delegations.own',
  settingsManage: 'platform.settings.manage',
  auditRead: 'audit.read',
  auditFinancialRead: 'audit.financial.read',
  documentsUpload: 'documents.upload',
  documentsDownload: 'documents.download',
  catalogManage: 'catalog.manage',
  referenceManage: 'reference.manage',
  customerReadOwn: 'crm.customer.read.own',
  customerReadAll: 'crm.customer.read.all',
  customerCreate: 'crm.customer.create',
  customerUpdate: 'crm.customer.update',
  customerTaxIdVerify: 'crm.customer.taxid.verify',
  classificationRequest: 'crm.customer.classification.request',
  classificationApprove: 'crm.customer.classification.override',
  customerReassign: 'crm.customer.reassign',
  leadManage: 'crm.lead.manage',
  opportunityManage: 'crm.opportunity.manage',
  quoteReadOwn: 'sales.quote.read.own',
  quoteReadAll: 'sales.quote.read.all',
  approvalStepApprove: 'approvals.step.approve',
  approvalReadAll: 'approvals.read.all',
} as const;
